import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Lock, Copy, Gift, ArrowRight, WhatsappLogo } from "@phosphor-icons/react";
import { ShareCardDialog } from "@/components/ShareCardDialog";
import { getDiwaliPackApi, claimDiwaliPackApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const TOOL = { chatgpt: "ChatGPT", claude: "Claude", gemini: "Gemini", midjourney: "Midjourney", sora: "Sora" };

function OpenCard({ p, onShare }) {
  const copy = async () => { await navigator.clipboard.writeText(p.prompt); toast.success("Prompt copied"); };
  return (
    <div className="stp-card flex flex-col rounded-3xl p-6" data-testid={`pack-card-${p.id}`}>
      <div className="flex items-center justify-between text-xs">
        <span className="rounded-full bg-[#FFF4E5] px-2.5 py-1 font-semibold text-[#C93400]">{p.category}</span>
        <span className="text-mist">for {TOOL[p.target_tool] || p.target_tool}</span>
      </div>
      <h3 className="mt-4 font-display text-lg font-semibold text-ink">{p.title}</h3>
      <p className="mt-2 line-clamp-5 flex-1 whitespace-pre-line font-mono text-[12px] leading-relaxed text-mist">{p.prompt}</p>
      <div className="mt-4 flex gap-2">
        <button data-testid={`pack-copy-${p.id}`} onClick={copy} className="flex h-9 items-center gap-1.5 rounded-full bg-black/5 px-3.5 text-xs font-medium text-ink hover:bg-black/10"><Copy size={13} /> Copy prompt</button>
        <button data-testid={`pack-share-${p.id}`} onClick={() => onShare(p)} className="flex h-9 items-center gap-1.5 rounded-full bg-[#25D366]/10 px-3.5 text-xs font-semibold text-[#128C4A] hover:bg-[#25D366]/20"><WhatsappLogo size={14} weight="fill" /> Share card</button>
      </div>
    </div>
  );
}

export function DiwaliPack() {
  const [pack, setPack] = useState(null);
  const [busy, setBusy] = useState(false);
  const [shareItem, setShareItem] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  useEffect(() => { getDiwaliPackApi().then(setPack).catch(() => {}); }, []);
  if (!pack) return null;
  const open = pack.items.filter((i) => !i.locked);
  const locked = pack.items.filter((i) => i.locked);

  const unlock = async () => {
    if (!user) return navigate("/login");
    setBusy(true);
    try {
      const r = await claimDiwaliPackApi();
      toast.success(r.added ? `${r.added} Diwali prompts added to your library` : "The pack is already in your library");
      navigate("/app");
    } catch { toast.error("Could not add the pack"); } finally { setBusy(false); }
  };

  return (
    <section className="py-28" data-testid="diwali-pack-section">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-[#FF9F0A] to-[#FF2D55] px-3 py-1 text-xs font-bold text-white"><Gift size={13} weight="fill" /> FREE · {pack.total} prompts</span>
          <h2 className="mt-4 max-w-2xl font-display text-4xl font-semibold leading-tight text-ink sm:text-5xl">The Diwali Prompt Pack.</h2>
          <p className="mt-3 max-w-2xl text-lg text-mist">Greetings, rangoli art, sale ads, sweets, party plans and family videos, ready to paste. Try 3 now. Sign up free to unlock all {pack.total} in your library.</p>
        </motion.div>
        <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-3">{open.map((p) => <OpenCard key={p.id} p={p} onShare={setShareItem} />)}</div>
        <div className="relative mt-5 overflow-hidden rounded-3xl bg-ink p-6 sm:p-8" data-testid="pack-locked">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            {locked.map((p) => (
              <div key={p.id} className="flex items-center gap-2 rounded-2xl bg-white/5 px-4 py-3 text-sm text-white/70">
                <Lock size={14} weight="fill" className="shrink-0 text-[#FF9F0A]" /> <span className="truncate">{p.title}</span>
              </div>
            ))}
          </div>
          <div className="mt-6 flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
            <p className="text-white/80">{locked.length} more festive prompts waiting for you.</p>
            <button data-testid="pack-unlock-button" onClick={unlock} disabled={busy} className="flex h-12 items-center gap-2 rounded-full bg-white px-6 font-semibold text-ink hover:bg-white/90 disabled:opacity-60">
              {user ? "Add all to my library" : `Sign up free to unlock all ${pack.total}`} <ArrowRight size={16} weight="bold" />
            </button>
          </div>
        </div>
      </div>
      <ShareCardDialog item={shareItem} open={!!shareItem} onOpenChange={(v) => !v && setShareItem(null)} />
    </section>
  );
}
